import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { DashboardLayout, DashboardWidget, DashboardWidgetType, UserPreferences } from '../types';

// Define the shape of the context
interface PreferencesContextType {
  preferences: UserPreferences;
  updateDashboardLayout: (layout: DashboardLayout) => void;
  toggleWidgetVisibility: (widgetId: DashboardWidgetType) => void;
  moveWidget: (widgetId: DashboardWidgetType, newPosition: number) => void;
  resizeWidget: (widgetId: DashboardWidgetType, newSize: 'small' | 'medium' | 'large') => void;
  changeWidgetColumn: (widgetId: DashboardWidgetType, column: 'left' | 'right') => void;
  resetDashboardLayout: () => void;
  updateTheme: (theme: 'light' | 'dark' | 'system') => void;
  updateCurrency: (currency: string) => void;
  updateDateFormat: (format: string) => void;
  reorderWidgets: (column: 'left' | 'right', widgets: DashboardWidget[]) => void;
}

// Default dashboard layout
const defaultDashboardLayout: DashboardLayout = {
  columns: 2,
  widgets: [
    { id: 'summary', title: 'Summary', enabled: true, position: 0, size: 'large' },
    { id: 'incomeExpenses', title: 'Income vs Expenses', enabled: true, position: 1, size: 'medium', column: 'left' },
    { id: 'accounts', title: 'Accounts Overview', enabled: true, position: 2, size: 'medium', column: 'left' },
    { id: 'transactions', title: 'Recent Transactions', enabled: true, position: 3, size: 'medium', column: 'left' },
    { id: 'expensesByCategory', title: 'Expenses by Category', enabled: true, position: 1, size: 'medium', column: 'right' },
    { id: 'upcomingBills', title: 'Upcoming Bills', enabled: true, position: 2, size: 'small', column: 'right' },
    { id: 'budgetStatus', title: 'Budget Status', enabled: true, position: 3, size: 'small', column: 'right' },
    { id: 'aiInsights', title: 'AI Insights', enabled: true, position: 4, size: 'small', column: 'right' },
    { id: 'earnings', title: 'Earnings & Cash In', enabled: false, position: 5, size: 'medium', column: 'left' },
    { id: 'expenses', title: 'Expenses & Liabilities', enabled: false, position: 5, size: 'medium', column: 'right' }
  ]
};

// Initial state
const initialPreferences: UserPreferences = {
  dashboardLayout: defaultDashboardLayout,
  theme: 'light',
  currency: 'USD',
  dateFormat: 'MM/DD/YYYY',
};

// Create the context
const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

// Action types
type PreferencesAction =
  | { type: 'UPDATE_DASHBOARD_LAYOUT'; payload: DashboardLayout }
  | { type: 'TOGGLE_WIDGET_VISIBILITY'; payload: DashboardWidgetType }
  | { type: 'MOVE_WIDGET'; payload: { widgetId: DashboardWidgetType; newPosition: number } }
  | { type: 'RESIZE_WIDGET'; payload: { widgetId: DashboardWidgetType; newSize: 'small' | 'medium' | 'large' } }
  | { type: 'CHANGE_WIDGET_COLUMN'; payload: { widgetId: DashboardWidgetType; column: 'left' | 'right' } }
  | { type: 'RESET_DASHBOARD_LAYOUT' }
  | { type: 'UPDATE_THEME'; payload: 'light' | 'dark' | 'system' }
  | { type: 'UPDATE_CURRENCY'; payload: string }
  | { type: 'UPDATE_DATE_FORMAT'; payload: string }
  | { type: 'REORDER_WIDGETS'; payload: { column: 'left' | 'right'; widgets: DashboardWidget[] } };

// Reducer function
const preferencesReducer = (state: UserPreferences, action: PreferencesAction): UserPreferences => {
  switch (action.type) {
    case 'UPDATE_DASHBOARD_LAYOUT':
      return {
        ...state,
        dashboardLayout: action.payload
      };
    case 'TOGGLE_WIDGET_VISIBILITY':
      return {
        ...state,
        dashboardLayout: {
          ...state.dashboardLayout,
          widgets: state.dashboardLayout.widgets.map(widget => 
            widget.id === action.payload 
              ? { ...widget, enabled: !widget.enabled } 
              : widget
          )
        }
      };
    case 'MOVE_WIDGET':
      return {
        ...state,
        dashboardLayout: {
          ...state.dashboardLayout,
          widgets: state.dashboardLayout.widgets.map(widget => 
            widget.id === action.payload.widgetId 
              ? { ...widget, position: action.payload.newPosition } 
              : widget
          )
        }
      };
    case 'RESIZE_WIDGET':
      return {
        ...state,
        dashboardLayout: {
          ...state.dashboardLayout,
          widgets: state.dashboardLayout.widgets.map(widget => 
            widget.id === action.payload.widgetId 
              ? { ...widget, size: action.payload.newSize } 
              : widget
          )
        }
      };
    case 'CHANGE_WIDGET_COLUMN':
      return {
        ...state,
        dashboardLayout: {
          ...state.dashboardLayout,
          widgets: state.dashboardLayout.widgets.map(widget => 
            widget.id === action.payload.widgetId 
              ? { ...widget, column: action.payload.column } 
              : widget
          )
        }
      };
    case 'RESET_DASHBOARD_LAYOUT':
      return {
        ...state,
        dashboardLayout: defaultDashboardLayout
      };
    case 'UPDATE_THEME':
      return {
        ...state,
        theme: action.payload
      };
    case 'UPDATE_CURRENCY':
      return {
        ...state,
        currency: action.payload
      };
    case 'UPDATE_DATE_FORMAT':
      return {
        ...state,
        dateFormat: action.payload
      };
    case 'REORDER_WIDGETS': {
      const { column, widgets: updatedColumnWidgets } = action.payload;
      
      // Get all widgets that are not in the specified column
      const otherWidgets = state.dashboardLayout.widgets.filter(
        widget => (widget.column || 'left') !== column
      );
      
      // Combine the updated column widgets with the other widgets
      return {
        ...state,
        dashboardLayout: {
          ...state.dashboardLayout,
          widgets: [...otherWidgets, ...updatedColumnWidgets]
        }
      };
    }
    default:
      return state;
  }
};

// Provider component
export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Load preferences from localStorage if available
  const loadPreferences = (): UserPreferences => {
    const savedPreferences = localStorage.getItem('userPreferences');
    if (savedPreferences) {
      try {
        return JSON.parse(savedPreferences);
      } catch (error) {
        console.error('Failed to parse saved preferences', error);
      }
    }
    return initialPreferences;
  };

  const [preferences, dispatch] = useReducer(preferencesReducer, loadPreferences());

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  // Update dashboard layout
  const updateDashboardLayout = (layout: DashboardLayout) => {
    dispatch({ type: 'UPDATE_DASHBOARD_LAYOUT', payload: layout });
  };

  // Toggle widget visibility
  const toggleWidgetVisibility = (widgetId: DashboardWidgetType) => {
    dispatch({ type: 'TOGGLE_WIDGET_VISIBILITY', payload: widgetId });
  };

  // Move widget to a new position
  const moveWidget = (widgetId: DashboardWidgetType, newPosition: number) => {
    dispatch({ 
      type: 'MOVE_WIDGET', 
      payload: { widgetId, newPosition } 
    });
  };

  // Resize widget
  const resizeWidget = (widgetId: DashboardWidgetType, newSize: 'small' | 'medium' | 'large') => {
    dispatch({ 
      type: 'RESIZE_WIDGET', 
      payload: { widgetId, newSize } 
    });
  };

  // Change widget column
  const changeWidgetColumn = (widgetId: DashboardWidgetType, column: 'left' | 'right') => {
    dispatch({ 
      type: 'CHANGE_WIDGET_COLUMN', 
      payload: { widgetId, column } 
    });
  };

  // Reset dashboard layout to default
  const resetDashboardLayout = () => {
    dispatch({ type: 'RESET_DASHBOARD_LAYOUT' });
  };

  // Update theme
  const updateTheme = (theme: 'light' | 'dark' | 'system') => {
    dispatch({ type: 'UPDATE_THEME', payload: theme });
  };

  // Update currency
  const updateCurrency = (currency: string) => {
    dispatch({ type: 'UPDATE_CURRENCY', payload: currency });
  };

  // Update date format
  const updateDateFormat = (format: string) => {
    dispatch({ type: 'UPDATE_DATE_FORMAT', payload: format });
  };

  // Reorder widgets in a column
  const reorderWidgets = (column: 'left' | 'right', widgets: DashboardWidget[]) => {
    dispatch({
      type: 'REORDER_WIDGETS',
      payload: { column, widgets }
    });
  };

  return (
    <PreferencesContext.Provider 
      value={{ 
        preferences, 
        updateDashboardLayout, 
        toggleWidgetVisibility, 
        moveWidget, 
        resizeWidget, 
        changeWidgetColumn, 
        resetDashboardLayout,
        updateTheme,
        updateCurrency,
        updateDateFormat,
        reorderWidgets
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

// Custom hook to use the preferences context
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};